import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type DateRange = { startDate?: string | Date; endDate?: string | Date };
export type FilterType = 'search' | 'date' | 'dropdown' | 'multi-dropdown' | 'checkbox';
export type SortOrder = 'asc' | 'desc';

export interface FilterConfig {
  key: string;
  label: string;
  type: FilterType;
  options?: string[];
  default?: any;
}

type UrlOverrides = Partial<{
  page: number;
  limit: number;
  sortColumn: string;
  sortOrder: SortOrder;
}>;

interface UseListQueryArgs {
  filtersConfig: readonly FilterConfig[] | undefined;
  defaultSortColumn: string;
  defaultSortOrder?: SortOrder;
  defaultPageSize?: number;
}

const getEmptyValueForType = (type: FilterType) => (type === 'multi-dropdown' ? [] : type === 'checkbox' ? false : '');

export const buildDefaultFilterValues = (filterConfigs: readonly FilterConfig[]) =>
  filterConfigs.reduce<Record<string, any>>((acc, cfg) => {
    acc[cfg.key] = cfg.default ?? getEmptyValueForType(cfg.type);
    return acc;
  }, {});

export const parseFiltersFromUrl = (filterConfigs: readonly FilterConfig[], searchParams: URLSearchParams) => {
  const parsedFilters: Record<string, any> = {};

  filterConfigs.forEach(({ key, type }) => {
    if (type === 'date') {
      const hasFrom = searchParams.has(`${key}_from`);
      const hasTo = searchParams.has(`${key}_to`);
      if (hasFrom || hasTo) {
        const from = searchParams.get(`${key}_from`) || undefined;
        const to = searchParams.get(`${key}_to`) || undefined;
        parsedFilters[key] = { startDate: from, endDate: to };
      }
      return;
    }

    if (type === 'multi-dropdown') {
      if (searchParams.has(key)) {
        const raw = searchParams.get(key);
        parsedFilters[key] = raw ? raw.split(',') : [];
      }
      return;
    }

    if (type === 'checkbox') {
      if (searchParams.has(key)) {
        parsedFilters[key] = searchParams.get(key) === 'true';
      }
      return;
    }

    if (searchParams.has(key)) {
      parsedFilters[key] = searchParams.get(key);
    }
  });

  return parsedFilters;
};

export const serializeFiltersToUrl = (filterConfigs: readonly FilterConfig[], filterState: Record<string, any>, searchParams: URLSearchParams) => {
  filterConfigs.forEach(({ key, type }) => {
    const filterValue = filterState?.[key];

    if (type === 'date') {
      searchParams.delete(`${key}_from`);
      searchParams.delete(`${key}_to`);
      if (filterValue?.startDate && filterValue?.endDate) {
        const fromIso = typeof filterValue.startDate === 'string' ? filterValue.startDate : filterValue.startDate.toISOString();
        const toIso = typeof filterValue.endDate === 'string' ? filterValue.endDate : filterValue.endDate.toISOString();
        searchParams.set(`${key}_from`, fromIso);
        searchParams.set(`${key}_to`, toIso);
      }
      return;
    }

    if (Array.isArray(filterValue)) {
      if (filterValue.length) {
        searchParams.set(key, filterValue.join(','));
      } else {
        searchParams.delete(key);
      }
      return;
    }

    if (typeof filterValue === 'boolean') {
      searchParams.set(key, String(filterValue));
      return;
    }

    if (filterValue) {
      searchParams.set(key, String(filterValue));
    } else {
      searchParams.delete(key);
    }
  });
};

export const useListQuery = ({ filtersConfig, defaultSortColumn, defaultSortOrder = 'asc', defaultPageSize = 100 }: UseListQueryArgs) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<Record<string, any>>({});
  const filtersRef = useRef<Record<string, any>>({});
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortOrder, setSortOrder] = useState<SortOrder>(defaultSortOrder);
  const [isHydrated, setIsHydrated] = useState(false);

  const [pageNumber, setPageNumber] = useState<number>(Number(searchParams.get('page') || 1));
  const [pageSize, setPageSize] = useState<number>(Number(searchParams.get('limit') || defaultPageSize));

  const lastNonEmptyFiltersConfigRef = useRef<readonly FilterConfig[] | null>(null);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (filtersConfig?.length) {
      lastNonEmptyFiltersConfigRef.current = filtersConfig;
    }
  }, [filtersConfig]);

  const hasHydratedRef = useRef(false);
  const prevFiltersLenRef = useRef<number | null>(null);

  useEffect(() => {
    const prevLen = prevFiltersLenRef.current;
    const nextLen = filtersConfig?.length ?? 0;
    prevFiltersLenRef.current = nextLen;
    if (prevLen === 0 && nextLen > 0) {
      hasHydratedRef.current = false;
    }
  }, [filtersConfig]);

  useEffect(() => {
    if (hasHydratedRef.current) return;

    if (!filtersConfig?.length) {
      const sc = searchParams.get('sortColumn') || defaultSortColumn;
      const so = (searchParams.get('sortOrder') as SortOrder) || defaultSortOrder;
      const pg = Number(searchParams.get('page') || 1);
      const lim = Number(searchParams.get('limit') || defaultPageSize);

      setSortColumn(sc);
      setSortOrder(so);
      setPageNumber(pg);
      setPageSize(lim);
      setIsHydrated(true);
      hasHydratedRef.current = true;
      return;
    }

    const defaults = buildDefaultFilterValues(filtersConfig);
    const fromUrl = parseFiltersFromUrl(filtersConfig, searchParams);
    const hydratedFilters = { ...defaults, ...fromUrl };
    filtersRef.current = hydratedFilters;
    setFilters(hydratedFilters);

    const sc = searchParams.get('sortColumn') || defaultSortColumn;
    const so = (searchParams.get('sortOrder') as SortOrder) || defaultSortOrder;
    const pg = Number(searchParams.get('page') || 1);
    const lim = Number(searchParams.get('limit') || defaultPageSize);
    setSortColumn(sc);
    setSortOrder(so);
    setPageNumber(pg);
    setPageSize(lim);
    setIsHydrated(true);

    if (!searchParams.get('sortColumn')) {
      const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : searchParams.toString());
      serializeFiltersToUrl(filtersConfig, { ...defaults, ...fromUrl }, params);
      if (!params.get('sortColumn')) params.set('sortColumn', sc);
      if (!params.get('sortOrder')) params.set('sortOrder', so);
      if (!params.get('page')) params.set('page', String(pg));
      if (!params.get('limit')) params.set('limit', String(lim));
      setTimeout(() => setSearchParams(params, { replace: true }), 0);
    }

    hasHydratedRef.current = true;
  }, [filtersConfig, searchParams, defaultSortColumn, defaultSortOrder, defaultPageSize, setSearchParams]);

  useEffect(() => {
    if (!hasHydratedRef.current) return;

    const sc = searchParams.get('sortColumn') || defaultSortColumn;
    const so = (searchParams.get('sortOrder') as SortOrder) || defaultSortOrder;
    const pg = Number(searchParams.get('page') || 1);
    const lim = Number(searchParams.get('limit') || defaultPageSize);

    if (sc !== sortColumn) setSortColumn(sc);
    if (so !== sortOrder) setSortOrder(so);
    if (pg !== pageNumber) setPageNumber(pg);
    if (lim !== pageSize) setPageSize(lim);

    if (!filtersConfig?.length) return;

    const defaults = buildDefaultFilterValues(filtersConfig);
    const fromUrl = parseFiltersFromUrl(filtersConfig, searchParams);
    const nextFilters = { ...defaults, ...fromUrl };

    if (JSON.stringify(nextFilters) !== JSON.stringify(filtersRef.current)) {
      filtersRef.current = nextFilters;
      setFilters(nextFilters);
    }
  }, [searchParams, filtersConfig, defaultSortColumn, defaultSortOrder, defaultPageSize, sortColumn, sortOrder, pageNumber, pageSize]);

  const syncUrlParams = useCallback(
    (overrides: UrlOverrides = {}, filtersSnapshot?: Record<string, any>) => {
      const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : searchParams.toString());

      const effectiveFiltersConfig = filtersConfig?.length ? filtersConfig : lastNonEmptyFiltersConfigRef.current;

      if (effectiveFiltersConfig?.length && (filtersSnapshot || filtersRef.current)) {
        serializeFiltersToUrl(effectiveFiltersConfig, filtersSnapshot ?? filtersRef.current, params);
      }

      params.set('sortColumn', overrides.sortColumn ?? sortColumn);
      params.set('sortOrder', overrides.sortOrder ?? sortOrder);
      params.set('page', String(overrides.page ?? pageNumber));
      params.set('limit', String(overrides.limit ?? pageSize));
      setSearchParams(params, { replace: true });
    },
    [filtersConfig, sortColumn, sortOrder, pageNumber, pageSize, setSearchParams, searchParams]
  );

  const setFilterValue = useCallback((key: string, value: any) => {
    const nextState = { ...filtersRef.current, [key]: value };
    filtersRef.current = nextState;
    setFilters(nextState);
  }, []);

  const resetFilters = useCallback(() => {
    if (!filtersConfig?.length) return {};
    const clearedFilters = buildDefaultFilterValues(filtersConfig);
    filtersRef.current = clearedFilters;
    setFilters(clearedFilters);
    setPageNumber(1);
    setPageSize(defaultPageSize);
    syncUrlParams({ page: 1, limit: defaultPageSize }, clearedFilters);
    return clearedFilters;
  }, [filtersConfig, syncUrlParams, defaultPageSize]);

  const updateSort = useCallback(
    (column: string, order: SortOrder) => {
      setSortColumn(column);
      setSortOrder(order);
      setPageNumber(1);
      syncUrlParams({ sortColumn: column, sortOrder: order, page: 1 });
    },
    [syncUrlParams]
  );

  const setFilterAndSync = useCallback(
    (key: string, value: any, overrides: UrlOverrides = { page: 1 }) => {
      const nextState: Record<string, any> = {
        ...filtersRef.current,
        [key]: value,
      };
      filtersRef.current = nextState;
      setFilters(nextState);
      if (typeof overrides.page === 'number') setPageNumber(overrides.page);
      if (typeof overrides.limit === 'number') setPageSize(overrides.limit);
      if (typeof overrides.sortColumn === 'string') {
        setSortColumn(overrides.sortColumn);
      }
      if (typeof overrides.sortOrder === 'string') {
        setSortOrder(overrides.sortOrder as SortOrder);
      }
      syncUrlParams(overrides, nextState);
    },
    [syncUrlParams]
  );

  const updatePage = useCallback(
    (newPage: number) => {
      setPageNumber(newPage);
      syncUrlParams({ page: newPage });
    },
    [syncUrlParams]
  );

  const updateLimit = useCallback(
    (newLimit: number) => {
      setPageSize(newLimit);
      setPageNumber(1);
      syncUrlParams({ limit: newLimit, page: 1 });
    },
    [syncUrlParams]
  );

  const queryPayload = useMemo(
    () => ({
      ...filters,
      sortColumn,
      sortOrder,
      page: pageNumber,
      limit: pageSize,
    }),
    [filters, sortColumn, sortOrder, pageNumber, pageSize]
  );

  return {
    filters,
    setFilter: setFilterValue,
    resetFilters,
    sortColumn,
    sortOrder,
    updateSort,
    page: pageNumber,
    limit: pageSize,
    updatePage,
    updateLimit,
    setFilterAndSync,
    payload: queryPayload,
    syncUrl: syncUrlParams,
    isHydrated,
  };
};
